/**
 * Floating overhead UI manager — owns per-frame lifecycle.
 *
 * For each entity in state.cubes / state.creatures / state.buildings:
 *   1. Visibility filter (alive, hp<hpMax for non-players, etc.)
 *   2. Sprite-bar layer: ensure exists, update values, position above head
 *   3. Rich-overlay layer: gated to "rich set" (own + allies + visible
 *      enemy players + targeted creature + boss + recently-damaged
 *      buildings). Project worldToScreen each frame.
 *
 * Spec: docs/superpowers/specs/2026-05-03-floating-overhead-ui-design.md
 */
import * as THREE from "three";
import type { HelloState, Cube, Creature, Building } from "@tribez/shared";
import { levelForXp } from "@tribez/shared";
import type { Player } from "../player";
import { createSpriteBar, type SpriteBar, type TeamRelation } from "./sprite-bar";
import { createRichOverlay, type RichOverlay } from "./rich-overlay";
import { creatureDisplayName } from "../display-names";
import {
  tickWeaponDotVfx, clearWeaponDotVfx, isWeaponDotBuff,
  tickLifestealVfx, clearLifestealVfx, tickAuraHealVfx, clearAuraHealVfx,
  tickWarmthVfx, clearWarmthVfx,
  tickCcOverlayVfx, clearCcOverlayVfx,
  tickCreatureCcOverlayVfx, clearCreatureCcOverlayVfx,
  tickCreatureDotOverlayVfx, clearCreatureDotOverlayVfx,
  tickCreatureDotVfx, clearCreatureDotVfx,
  tickCastProcVfx, clearCastProcVfx,
  tickSummonPuffVfx, clearSummonPuffVfx,
} from "../items/item-effect-vfx";

const HEAD_OFFSET_Y = 2.4;
const DAMAGED_BUILDING_DECAY_MS = 3000;

// Per-kind float height for the harvest HP bar (playtest 2026-06-12: "trees being
// harvested should have a health bar"). Tall trees need the bar up in the canopy;
// deposits/bushes sit low. Only these harvestable kinds get a bar — drops and
// growing saplings are excluded (a sapling's rising hp isn't "damage").
const RESOURCE_BAR_OFFSET: Record<string, number> = {
  ElderTree: 6.0,
  MapleTree: 3.0,
  StoneDeposit: 1.6,
  GoldDeposit: 1.6,
  BerryBush: 1.3,
  CulinaryHerbBush: 1.3,
};

// Harvestable trees show their HP bar PERSISTENTLY — always on while the tree is
// alive, even at full health — so players can read a tree's remaining yield at a
// glance before committing to chop it. Deposits/bushes keep the damage-only
// convention (a bar appears only after the first hit). (Aviv polish, 2026-06-29)
const PERSISTENT_BAR_KINDS = new Set(["ElderTree", "MapleTree"]);

export interface FloaterManager {
  tick(args: TickArgs): void;
  destroy(): void;
}

interface TickArgs {
  state: HelloState;
  mySessionId: string | null;
  camera: THREE.Camera;
  sampleHeight: (x: number, z: number) => number;
  /** Currently-targeted entity id (creature or player) — surfaces creature
      rich overlays. null when nothing locked. */
  attackTargetId: string | null;
  /** Client-side player objects keyed by sessionId. Positions here are
      lerped (POS_LERP=0.25) toward the server schema position each frame,
      so reading .object.position keeps bars/overlays in sync with the
      visible mesh instead of teleporting to each new server tick. */
  players: Map<string, Player>;
  /** Returns whether a world point is in LIVE fog vision; used to gate
      resource HP bars (only show what you currently see). Optional so
      callers/tests without fog still work. */
  isVisibleAt?: (x: number, z: number) => boolean;
}

interface SpriteEntry {
  bar: SpriteBar;
  rows: 1 | 3;  // dimension cache so we know if reconfig needed
}

interface BuildingDamageState {
  lastHp: number;
  damagedAt: number;
}

export function createFloaterManager(scene: THREE.Scene, layerEl: HTMLElement): FloaterManager {
  const cubeBars = new Map<string, SpriteEntry>();
  const creatureBars = new Map<string, SpriteEntry>();
  const buildingBars = new Map<string, SpriteEntry>();
  const resourceBars = new Map<string, SpriteEntry>();

  const cubeOverlays = new Map<string, RichOverlay>();
  const creatureOverlays = new Map<string, RichOverlay>();
  const buildingOverlays = new Map<string, RichOverlay>();

  const buildingDamage = new Map<string, BuildingDamageState>();

  // Summon puff VFX: tracks creature IDs already seen so first-appearance
  // can trigger the spawn-puff animation (Task 8).
  const knownSummonIds = new Set<string>();

  const tmpPos = new THREE.Vector3();

  function getOrCreateBar(
    id: string,
    map: Map<string, SpriteEntry>,
    rows: 1 | 3,
  ): SpriteEntry {
    const existing = map.get(id);
    if (existing && existing.rows === rows) return existing;
    if (existing) {
      // Row count changed — destroy and rebuild (rare; happens if a unit
      // changes class, but at v1 we don't, so this is just defensive).
      scene.remove(existing.bar.sprite);
      existing.bar.dispose();
    }
    // Players (3-row) carry the level badge in their left gutter; creatures /
    // buildings / resources (1-row) don't.
    const bar = createSpriteBar(
      rows === 3 ? ["hp", "mana", "stam"] : ["hp"],
      { levelGutter: rows === 3 },
    );
    scene.add(bar.sprite);
    const entry: SpriteEntry = { bar, rows };
    map.set(id, entry);
    return entry;
  }

  function dropBar(id: string, map: Map<string, SpriteEntry>): void {
    const e = map.get(id);
    if (!e) return;
    scene.remove(e.bar.sprite);
    e.bar.dispose();
    map.delete(id);
  }

  function getOrCreateOverlay(id: string, map: Map<string, RichOverlay>): RichOverlay {
    const existing = map.get(id);
    if (existing) return existing;
    const overlay = createRichOverlay(layerEl);
    map.set(id, overlay);
    return overlay;
  }

  function dropOverlay(id: string, map: Map<string, RichOverlay>): void {
    const o = map.get(id);
    if (!o) return;
    o.destroy();
    map.delete(id);
  }

  function tick(args: TickArgs): void {
    const { state, mySessionId, camera, sampleHeight, attackTargetId, players, isVisibleAt } = args;
    const now = Date.now();

    const me = (mySessionId ? state.cubes?.get(mySessionId) : null) ?? null;
    const myTeamId = me?.teamId ?? -1;

    // === Cubes (players / dummies) ===
    const seenCubes = new Set<string>();
    // @view()-gated maps (cubes/creatures/buildings/corpses) are `undefined` on
    // the client until the StateView holds ≥1 member of that map. On the
    // lobby→running transition an empty map (e.g. no buildings yet) arrives as a
    // delta and is never instantiated, so guard every per-frame iteration.
    state.cubes?.forEach((cube, sessionId) => {
      seenCubes.add(sessionId);
      if (cube.state !== "alive") {
        dropBar(sessionId, cubeBars);
        dropOverlay(sessionId, cubeOverlays);
        clearWeaponDotVfx(sessionId);
        clearLifestealVfx(sessionId);
        clearAuraHealVfx(sessionId);
        clearWarmthVfx(sessionId);
        clearCcOverlayVfx(sessionId);
        return;
      }

      const relation = teamRelation(cube, me, sessionId === mySessionId);

      // Prefer the lerped client-side position so bars track the visible mesh
      // smoothly. Falls back to schema position if the Player object hasn't
      // been created yet (only on first frame after onAdd), OR if the lerped
      // position is NaN (can happen when schema decode was corrupted by a
      // refId error on first delta — cube.x is always the authoritative value).
      const playerObj = players.get(sessionId);
      const lerpX = playerObj?.object.position.x;
      const lerpZ = playerObj?.object.position.z;
      const px = (lerpX != null && !isNaN(lerpX)) ? lerpX : cube.x;
      const pz = (lerpZ != null && !isNaN(lerpZ)) ? lerpZ : cube.z;

      const xp0 = Number.isFinite(cube.xp) ? cube.xp : 0;
      const level = cube.expertise?.characterLevel ?? levelForXp(xp0);  // B5: real KTS curve

      const entry = getOrCreateBar(sessionId, cubeBars, 3);
      const y = sampleHeight(px, pz) + HEAD_OFFSET_Y;
      entry.bar.setPosition(px, y, pz);
      // cube.survival may be undefined before first Colyseus delta — default stamina to 100
      const stamina = cube.survival?.stamina ?? 100;
      entry.bar.update(
        {
          hp: { cur: cube.hp, max: cube.hpMax },
          // Transient mana lives on the spellbook sub-schema (cube.spellbook.mana);
          // fall back to full only before that delta arrives.
          mana: { cur: cube.spellbook?.mana ?? cube.manaMax, max: cube.manaMax },
          stam: { cur: stamina, max: 100 },
          level,
        },
        relation,
      );
      entry.bar.setVisible(true);

      // --- Rich overlay: every alive cube (own + allies + enemies). Off-screen
      // culling is handled inside rich-overlay.project() via NDC bounds, so we
      // don't need a viewport filter here. Costs one DOM node per known cube;
      // off-screen ones flip to display:none immediately on first project().
      const overlay = getOrCreateOverlay(sessionId, cubeOverlays);
      const behaviors = new Map<string, number>();
      cube.meta?.behaviors?.forEach((entry, id) => behaviors.set(id, entry.expiresAt));
      // Merge active weapon-DoT buffs (cube.spellbook.buffs) into the strip so a
      // poisoned/burning unit shows the matching debuff icon (registry.ts has
      // their strip-icon presentation).
      cube.spellbook?.buffs?.forEach((inst, buffId) => {
        if (isWeaponDotBuff(buffId)) behaviors.set(buffId, inst.expiresAt);
      });
      overlay.update({
        name: cube.meta?.name || `Player ${sessionId.slice(0, 4)}`,
        level,  // now rendered in the bar's left gutter, not the overlay
        relation,
        behaviors,
        now,
      });
      const overlayY = sampleHeight(px, pz) + HEAD_OFFSET_Y + 0.6;
      tmpPos.set(px, overlayY, pz);
      overlay.project(tmpPos, camera);

      // Weapon-DoT VFX: float colored damage numbers on HP drop while a DoT is
      // active + set the debuff tint (data-dot) on the overlay root. Reuse the
      // bar's projected head position for the floater screen coords.
      tmpPos.set(px, y, pz);
      const ndc = tmpPos.project(camera);
      const sx = (ndc.x * 0.5 + 0.5) * window.innerWidth;
      const sy = (-ndc.y * 0.5 + 0.5) * window.innerHeight;
      const onScreen = ndc.z >= -1 && ndc.z <= 1 && Math.abs(ndc.x) <= 1.2 && Math.abs(ndc.y) <= 1.2;
      const dotTint = tickWeaponDotVfx(cube, sessionId, layerEl, sx, sy);
      if (dotTint && onScreen) overlay.el.dataset.dot = dotTint;
      else delete overlay.el.dataset.dot;

      // Lifesteal VFX: green "+N" floater on the ATTACKER when its HP rises from
      // a lifesteal heal. Aura-heal VFX: green "+N" + sparkle on an ALLY when its
      // HP rises while it carries the CelestialOrbCooldown ICD buff. Both mirror
      // the DoT detection pattern (per-frame HP delta), gated to on-screen units.
      if (onScreen) {
        tickLifestealVfx(cube, sessionId, layerEl, sx, sy);
        tickAuraHealVfx(cube, sessionId, layerEl, sx, sy);
        // Warmth restore floater — own cube only (warmth is a survival meter
        // not shown on other players; floater on self is sufficient feedback).
        if (sessionId === mySessionId) {
          tickWarmthVfx(cube, sessionId, layerEl, sx, sy);
        }
        // CC overlay — stun/daze/slow/silence/fear icon above the unit (Task 6).
        // Shown on all alive cubes that carry a CC buff; own cube included so the
        // player can see their own CC state (stunned while reading the screen).
        tickCcOverlayVfx(cube, sessionId, layerEl, sx, sy);
        // Cast-proc buff floaters — Task 10 (Family 5: Liquidator/RockAxe/MysticShield/Dismantle).
        // First-appearance detection: spawns a colored "✦" floater on the frame a
        // cast-proc buff first appears on this cube.
        tickCastProcVfx(cube, sessionId, layerEl, sx, sy);
      }
    });
    // Cull entries for cubes that left state
    for (const id of cubeBars.keys()) if (!seenCubes.has(id)) { dropBar(id, cubeBars); clearWeaponDotVfx(id); clearLifestealVfx(id); clearAuraHealVfx(id); clearWarmthVfx(id); clearCcOverlayVfx(id); clearCastProcVfx(id); }
    for (const id of cubeOverlays.keys()) if (!seenCubes.has(id)) dropOverlay(id, cubeOverlays);

    // === Creatures ===
    const seenCreatures = new Set<string>();
    state.creatures?.forEach((creature, id) => {
      seenCreatures.add(id);
      if (creature.aiState === "dead" || creature.hp <= 0) {
        dropBar(id, creatureBars);
        dropOverlay(id, creatureOverlays);
        clearCreatureCcOverlayVfx(id);
        clearCreatureDotOverlayVfx(id);
        clearCreatureDotVfx(id);
        return;
      }

      const entry = getOrCreateBar(id, creatureBars, 1);
      const y = sampleHeight(creature.x, creature.z) + HEAD_OFFSET_Y;
      entry.bar.setPosition(creature.x, y, creature.z);
      entry.bar.update(
        { hp: { cur: creature.hp, max: creature.hpMax } },
        "neutral",
      );
      entry.bar.setVisible(true);

      // Summon puff VFX: detect first-appearance of owned summons (Task 8).
      const isOwnSummon = creature.ownerId === mySessionId;
      const csh = sampleHeight(creature.x, creature.z);
      tmpPos.set(creature.x, csh + 1.2, creature.z);
      const ndcC = tmpPos.project(camera);
      const csx = (ndcC.x * 0.5 + 0.5) * window.innerWidth;
      const csy = (-ndcC.y * 0.5 + 0.5) * window.innerHeight;
      tickSummonPuffVfx(id, knownSummonIds, isOwnSummon, layerEl, csx, csy);

      // For CC/DoT overlays project from the bar height (HEAD_OFFSET_Y), matching
      // the cube CC overlay pattern, so markers sit above the HP bar not on it.
      tmpPos.set(creature.x, csh + HEAD_OFFSET_Y, creature.z);
      const ndcCBar = tmpPos.project(camera);
      const cosx = (ndcCBar.x * 0.5 + 0.5) * window.innerWidth;
      const cosy = (-ndcCBar.y * 0.5 + 0.5) * window.innerHeight;

      // Creature CC overlay (item-effects creature-CC): stun-stars over a
      // stunned/disarmed/slowed creature, read off the synced incapUntil field.
      tickCreatureCcOverlayVfx(id, creature.incapUntil ?? 0, now, layerEl, cosx, cosy);
      // Creature DoT overlay (item-effects creature-DoT): poison/fire marker over
      // a creature carrying an active weapon/ability DoT (synced dotUntil+dotKind).
      tickCreatureDotOverlayVfx(id, creature.dotUntil ?? 0, creature.dotKind ?? "", now, layerEl, cosx, cosy);
      // Creature DoT number floater: rising -N tinted by dotKind on each HP drop
      // while a DoT is active (mirrors tickWeaponDotVfx for cube DoT numbers).
      tickCreatureDotVfx(id, creature.hp, creature.dotUntil ?? 0, creature.dotKind ?? "", layerEl, csx, csy);

      // Rich overlay: only when targeted (boss flag is server-side and not
      // exposed via Creature schema — keep "boss" deferred until that lands).
      if (id === attackTargetId) {
        const overlay = getOrCreateOverlay(id, creatureOverlays);
        overlay.update({
          name: creatureDisplayName(creature.creatureType),
          level: 1,
          relation: "neutral",
          behaviors: new Map(),  // creatures don't carry behaviors at v1
          now,
        });
        const y = sampleHeight(creature.x, creature.z) + HEAD_OFFSET_Y + 0.6;
        tmpPos.set(creature.x, y, creature.z);
        overlay.project(tmpPos, camera);
      } else {
        dropOverlay(id, creatureOverlays);
      }
    });
    for (const id of creatureBars.keys()) if (!seenCreatures.has(id)) { dropBar(id, creatureBars); clearSummonPuffVfx(id, knownSummonIds); clearCreatureCcOverlayVfx(id); clearCreatureDotOverlayVfx(id); clearCreatureDotVfx(id); }
    for (const id of creatureOverlays.keys()) if (!seenCreatures.has(id)) dropOverlay(id, creatureOverlays);

    // === Buildings ===
    const seenBuildings = new Set<string>();
    state.buildings?.forEach((b, id) => {
      seenBuildings.add(id);
      if (b.state === "destroyed") {
        dropBar(id, buildingBars);
        dropOverlay(id, buildingOverlays);
        buildingDamage.delete(id);
        return;
      }

      // Track damage transitions so the rich overlay surfaces for ~3s after a hit
      const dmg = buildingDamage.get(id);
      if (dmg) {
        if (b.hp < dmg.lastHp) {
          dmg.damagedAt = now;
          dmg.lastHp = b.hp;
        } else if (b.hp > dmg.lastHp) {
          dmg.lastHp = b.hp;
        }
      } else {
        buildingDamage.set(id, { lastHp: b.hp, damagedAt: 0 });
      }

      // Bar: only when damaged
      if (b.hp < b.hpMax) {
        const entry = getOrCreateBar(id, buildingBars, 1);
        const y = sampleHeight(b.x, b.z) + HEAD_OFFSET_Y;
        entry.bar.setPosition(b.x, y, b.z);
        entry.bar.update(
          { hp: { cur: b.hp, max: b.hpMax } },
          b.teamId === myTeamId ? "ally" : "enemy",
        );
        entry.bar.setVisible(true);
      } else {
        dropBar(id, buildingBars);
      }

      // Rich overlay: damaged within last 3s
      const damagedRecently = (buildingDamage.get(id)?.damagedAt ?? 0) > now - DAMAGED_BUILDING_DECAY_MS;
      if (damagedRecently) {
        const overlay = getOrCreateOverlay(id, buildingOverlays);
        overlay.update({
          name: b.buildingType,
          level: 1,
          relation: b.teamId === myTeamId ? "ally" : "enemy",
          behaviors: new Map(),
          now,
        });
        const y = sampleHeight(b.x, b.z) + HEAD_OFFSET_Y + 0.6;
        tmpPos.set(b.x, y, b.z);
        overlay.project(tmpPos, camera);
      } else {
        dropOverlay(id, buildingOverlays);
      }
    });
    for (const id of buildingBars.keys()) if (!seenBuildings.has(id)) { dropBar(id, buildingBars); buildingDamage.delete(id); }
    for (const id of buildingOverlays.keys()) if (!seenBuildings.has(id)) dropOverlay(id, buildingOverlays);

    // === Resources (harvest HP bars) ===
    // Trees show their bar persistently (always-on while alive); deposits/bushes
    // show only once they've taken a hit, matching the building convention.
    const seenResources = new Set<string>();
    state.resources.forEach((node, id) => {
      const offset = RESOURCE_BAR_OFFSET[node.kind];
      const persistent = PERSISTENT_BAR_KINDS.has(node.kind);
      if (offset === undefined || node.hp <= 0 || (!persistent && node.hp >= node.hpMax)) {
        dropBar(id, resourceBars);
        return;
      }
      // Resource nodes stay rendered in explored areas (map memory), but their
      // HP bar is live-vision-only — you shouldn't learn a remembered tree is
      // being chopped by someone you can't see.
      if (isVisibleAt && !isVisibleAt(node.x, node.z)) {
        dropBar(id, resourceBars);
        return;
      }
      seenResources.add(id);
      const entry = getOrCreateBar(id, resourceBars, 1);
      const y = sampleHeight(node.x, node.z) + offset;
      entry.bar.setPosition(node.x, y, node.z);
      entry.bar.update({ hp: { cur: node.hp, max: node.hpMax } }, "neutral");
      entry.bar.setVisible(true);
    });
    for (const id of resourceBars.keys()) if (!seenResources.has(id)) dropBar(id, resourceBars);
  }

  function destroy(): void {
    for (const e of cubeBars.values()) { scene.remove(e.bar.sprite); e.bar.dispose(); }
    for (const e of creatureBars.values()) { scene.remove(e.bar.sprite); e.bar.dispose(); }
    for (const e of buildingBars.values()) { scene.remove(e.bar.sprite); e.bar.dispose(); }
    for (const e of resourceBars.values()) { scene.remove(e.bar.sprite); e.bar.dispose(); }
    for (const o of cubeOverlays.values()) o.destroy();
    for (const o of creatureOverlays.values()) o.destroy();
    for (const o of buildingOverlays.values()) o.destroy();
    cubeBars.clear(); creatureBars.clear(); buildingBars.clear(); resourceBars.clear();
    cubeOverlays.clear(); creatureOverlays.clear(); buildingOverlays.clear();
    knownSummonIds.clear();
  }

  return { tick, destroy };
}

function teamRelation(cube: Cube, me: Cube | null, isOwn: boolean): TeamRelation {
  if (isOwn) return "ally";
  if (cube.teamId === 99) return "neutral";
  if (!me) return "enemy";
  return cube.teamId === me.teamId ? "ally" : "enemy";
}
